import React, { useState, useEffect, useMemo } from 'react';
import type { QuickAddCombo, MenuItem, Category } from '../../types';
import XCircleIcon from '../icons/XCircleIcon';
import StarIcon from '../icons/StarIcon';
import ZapIcon from '../icons/ZapIcon';

interface QuickAddComboModalProps {
  combo: QuickAddCombo | null;
  menuItems: MenuItem[];
  categories: Category[];
  onClose: () => void;
  onSave: (combo: Omit<QuickAddCombo, 'id'> | QuickAddCombo) => void;
}

const QuickAddComboModal: React.FC<QuickAddComboModalProps> = ({ combo, menuItems, categories, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [iconType, setIconType] = useState<'zap' | 'star'>('zap');
  const [selectedItems, setSelectedItems] = useState<{ menuItemId: number; quantity: number }[]>([]);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [comboPrice, setComboPrice] = useState<number | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(categories[0]?.id ?? null);

  // Group menu items by category (category id can be number or string)
  const itemsByCategory = useMemo(() => {
    const grouped: Record<string, MenuItem[]> = {};
    categories.forEach(cat => {
      const key = String(cat.id);
      grouped[key] = menuItems.filter(item => String(item.categoryId) === key);
    });
    return grouped;
  }, [menuItems, categories]);

  // Get items for selected category
  const categoryItems = selectedCategoryId ? (itemsByCategory[selectedCategoryId] || []) : [];

  useEffect(() => {
    if (combo) {
      setName(combo.name);
      setIconType(combo.iconType);
      setSelectedItems(combo.items);
      setDisplayOrder(combo.displayOrder || 0);
      setComboPrice(combo.price);
    } else {
      setName('');
      setIconType('zap');
      setSelectedItems([]);
      setDisplayOrder(0);
      setComboPrice(undefined);
    }
  }, [combo]);

  // Calculate total price of individual items
  const individualItemsTotal = useMemo(() => {
    return selectedItems.reduce((total, item) => {
      const menuItem = menuItems.find(m => m.id === item.menuItemId);
      return total + (menuItem ? menuItem.price * item.quantity : 0);
    }, 0);
  }, [selectedItems, menuItems]);

  // Calculate savings
  const savings = comboPrice && comboPrice < individualItemsTotal 
    ? individualItemsTotal - comboPrice 
    : 0;

  const handleAddItem = (menuItemId?: number) => {
    if (menuItems.length === 0) {
      alert('No menu items available. Please add menu items first.');
      return;
    }
    const itemId = menuItemId || (categoryItems.length > 0 ? categoryItems[0].id : menuItems[0].id);
    setSelectedItems([...selectedItems, { menuItemId: itemId, quantity: 1 }]);
  };

  const handleRemoveItem = (index: number) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index: number, field: 'menuItemId' | 'quantity', value: number) => {
    const updated = [...selectedItems];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedItems(updated);
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a combo name');
      return;
    }
    if (selectedItems.length === 0) {
      alert('Please add at least one menu item to the combo');
      return;
    }

    const comboData = {
      ...(combo ? { id: combo.id } : {}),
      name: name.trim(),
      iconType,
      displayOrder,
      price: comboPrice && comboPrice > 0 ? comboPrice : undefined,
      items: selectedItems,
    };

    onSave(comboData as any);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {combo ? 'Edit Quick Add Combo' : 'Create Quick Add Combo'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Combo Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Combo Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="e.g., Burger Combo"
            />
          </div>

          {/* Icon Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Icon Type
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setIconType('zap')}
                className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg transition-colors ${
                  iconType === 'zap'
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ZapIcon className="w-5 h-5" />
                <span>Lightning (⚡)</span>
              </button>
              <button
                type="button"
                onClick={() => setIconType('star')}
                className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg transition-colors ${
                  iconType === 'star'
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <StarIcon className="w-5 h-5" />
                <span>Star (⭐)</span>
              </button>
            </div>
          </div>

          {/* Combo Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Combo Price (Optional)
            </label>
            <input
              type="number"
              step="0.01"
              value={comboPrice || ''}
              onChange={(e) => setComboPrice(e.target.value ? parseFloat(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              min="0"
              placeholder="Leave empty to use sum of items"
            />
            {selectedItems.length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 rounded-md text-xs">
                <div className="flex justify-between text-gray-600 mb-1">
                  <span>Individual items total:</span>
                  <span className="font-medium">${individualItemsTotal.toFixed(2)}</span>
                </div>
                {comboPrice && comboPrice > 0 && (
                  <>
                    <div className="flex justify-between text-gray-600 mb-1">
                      <span>Combo price:</span>
                      <span className="font-medium text-pink-600">${comboPrice.toFixed(2)}</span>
                    </div>
                    {savings > 0 && (
                      <div className="flex justify-between text-green-600 font-semibold pt-1 border-t border-gray-300">
                        <span>Customer saves:</span>
                        <span>${savings.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Set a combo price to offer a discount. If left empty, the sum of individual item prices will be used.
            </p>
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Order
            </label>
            <input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
          </div>

          {/* Combo Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Menu Items *
              </label>
            </div>

            {/* Category Selector */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Browse by Category
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => {
                  const itemCount = itemsByCategory[String(cat.id)]?.length || 0;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategoryId(cat.id)}
                      className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-colors ${
                        String(selectedCategoryId) === String(cat.id)
                          ? 'border-pink-500 bg-pink-50 text-pink-700 font-semibold'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {cat.name} ({itemCount})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Items from Selected Category */}
            {selectedCategoryId && categoryItems.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {categories.find(c => c.id === selectedCategoryId)?.name} Items
                </h4>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {categoryItems.map(menuItem => (
                    <button
                      key={menuItem.id}
                      type="button"
                      onClick={() => handleAddItem(menuItem.id)}
                      className="flex items-center justify-between p-2 bg-white border border-gray-300 rounded-md hover:bg-pink-50 hover:border-pink-300 transition-colors text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{menuItem.name}</p>
                        <p className="text-xs text-gray-500">${menuItem.price.toFixed(2)}</p>
                      </div>
                      <span className="ml-2 text-pink-600 font-bold">+</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Items List */}
            {selectedItems.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center border-2 border-dashed border-gray-300 rounded-lg">
                No items added. Select a category above and click on items to add them to the combo.
              </p>
            ) : (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Selected Items</h4>
                {selectedItems.map((item, index) => {
                  const menuItem = menuItems.find(m => m.id === item.menuItemId);
                  const itemCategory = categories.find(c => String(c.id) === String(menuItem?.categoryId));
                  return (
                    <div key={`combo-item-${item.menuItemId}-${index}`} className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg bg-white">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{menuItem?.name || `Item #${item.menuItemId}`}</p>
                        <p className="text-xs text-gray-500">
                          {itemCategory?.name} • ${menuItem?.price.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        min="1"
                        className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                        placeholder="Qty"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="px-2 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
          >
            {combo ? 'Update Combo' : 'Create Combo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickAddComboModal;

